/**
 * The closed diagnostic code registry — the machine side of a repair receipt.
 *
 * A diagnostic's `code` used to be a free string typed at the emit site, so no
 * consumer could enumerate what it might see, and nothing bound a code to what
 * REPAIRS it. Every code an ArchLang surface emits is declared here, once, with:
 *
 *  - **`fixes`** — the repairs that address it, each naming the input it edits.
 *    Two targets, and the distinction is the whole point: a `model` fix edits
 *    `.arch` source the author owns; a `solver` fix changes an input the LAYOUT
 *    consumes (a separation floor, a corridor reservation, a pin). With an
 *    algorithmic layout a composition violation is the solver's fault, so its
 *    repair can never be "move the box" — it names a solver input instead.
 *  - **`suppresses`** — the symptom codes this one is the root cause OF. When
 *    both appear in the same file, the symptom is dropped: an agent looping on
 *    a receipt should repair the cause, not chase the ten diagnostics it made.
 *
 * `DiagnosticCode` is the union of the keys, and `Diagnostic.code` is typed to
 * it — an unregistered code is a compile error at the emit site, which is what
 * keeps the registry closed.
 */

// =============================================================================
// Repair vocabulary
// =============================================================================

/** What a repair edits. `model` = `.arch` source (or the package manifest);
 *  `solver` = an input the layout consumes before it places anything. */
export type RepairTarget = "model" | "solver";

/** One repair a code supports. `id` is stable and machine-actionable (an agent
 *  or a quick-fix keys on it); `input` names the model or solver input the
 *  repair edits, in the vocabulary of the thing being edited. */
export interface RepairFix {
  readonly id: string;
  readonly target: RepairTarget;
  readonly input: string;
}

/**
 * One model fact that proves a diagnostic. The registry's third axis, next to
 * `fixes` and `suppresses`: `params` say WHAT was flagged, evidence says WHY the
 * analysis is entitled to flag it — the nodes and edges a reader re-checks by
 * hand. Kept here (not per-producer) so every finding surface — governance
 * today, geometry next — reports proof in ONE shape.
 *
 * `ref` is the producer's stable display key for the fact: a node path, or
 * `<source> > <target>` for an edge. `note` is the fact's role in the proof
 * ("subject", "missing aspect team"), never a restatement of the message.
 */
export interface DiagnosticEvidence {
  readonly kind: "node" | "edge";
  readonly ref: string;
  readonly note?: string;
}

export interface DiagCodeSpec {
  /** Repairs, most-direct first. Absent = no mechanical repair is claimed. */
  readonly fixes?: readonly RepairFix[];
  /** Codes this one is the root cause of — silenced while this one stands. */
  readonly suppresses?: readonly string[];
}

// Shared fix descriptors, so identical repairs stay identical across codes.
const QUALIFY_REF: RepairFix = { id: "qualify-reference", target: "model", input: "the reference path" };
const RENAME_DECL: RepairFix = { id: "rename-declaration", target: "model", input: "the declaration name" };
const DECLARE_TARGET: RepairFix = { id: "declare-target", target: "model", input: "a new declaration" };
const REMOVE_DECL: RepairFix = { id: "remove-declaration", target: "model", input: "the duplicate declaration" };
const FILL_REQUIRED: RepairFix = { id: "fill-required-field", target: "model", input: "the module body" };
const ADD_OVERRIDE: RepairFix = { id: "add-override", target: "model", input: "the member's `override` marker" };
const EXPORT_DECL: RepairFix = { id: "export-declaration", target: "model", input: "the declaration's `export` marker" };

// Solver-side repairs. Every geometry violation is the layout's, so these name
// what `stressLayout` (and the port/corridor pass) reads, never a coordinate.
const RAISE_SEPARATION: RepairFix = {
  id: "raise-separation",
  target: "solver",
  input: "stressLayout separation floor",
};
const RESERVE_CORRIDOR: RepairFix = {
  id: "reserve-corridor",
  target: "solver",
  input: "clearPortCorridors corridor reservation",
};
const GROW_NODE: RepairFix = { id: "grow-node", target: "solver", input: "the node's composed size" };
const PIN_NODE: RepairFix = { id: "pin-node", target: "model", input: "view … style pin x, y" };

// =============================================================================
// The registry
// =============================================================================

export const DIAG_CODES = {
  // --- Parse -----------------------------------------------------------------
  PARSE_ERROR: {},

  // --- Package / workspace loading -------------------------------------------
  // A package that never loaded is the root cause of every failure to find a
  // type IN it. Deliberately NOT of `UNKNOWN_TYPE`: that fires for kinds the
  // missing `use` never named, and silencing it would hide unrelated work.
  USE_PACKAGE_NOT_FOUND: {
    fixes: [{ id: "add-dependency", target: "model", input: "package.archspace dependencies" }],
    suppresses: ["USE_TYPE_NOT_FOUND", "USE_TYPE_NOT_EXPORTED"],
  },
  USE_TYPE_NOT_FOUND: { fixes: [QUALIFY_REF] },
  USE_TYPE_NOT_EXPORTED: { fixes: [EXPORT_DECL] },
  USE_NAME_COLLISION: { fixes: [{ id: "alias-import", target: "model", input: "the `use` clause alias" }] },
  USE_POLICY_NOT_FOUND: { fixes: [QUALIFY_REF] },
  USE_POLICY_NOT_EXPORTED: { fixes: [EXPORT_DECL] },
  USE_POLICY_PACKAGE_NOT_FOUND: {
    fixes: [{ id: "add-dependency", target: "model", input: "package.archspace dependencies" }],
    suppresses: ["USE_POLICY_NOT_FOUND", "USE_POLICY_NOT_EXPORTED"],
  },
  DEP_CYCLE: {},
  DEP_LOAD_FAILED: { suppresses: ["USE_TYPE_NOT_FOUND", "USE_TYPE_NOT_EXPORTED"] },
  DEP_NAME_MISMATCH: {},
  MANIFEST_PARSE_ERROR: {},
  RESERVED_PACKAGE_NAMESPACE: {},
  STDLIB_NOT_FOUND: {},
  WIDGETS_FILE_NOT_FOUND: {},
  WORKSPACE_LOAD_FAILED: {},
  FILE_READ_ERROR: {},
  NOT_ARCHLANG: {},

  // --- Resolution ------------------------------------------------------------
  // A duplicate declaration is why a reference to it is ambiguous.
  DUPLICATE_MODULE: { fixes: [RENAME_DECL, REMOVE_DECL], suppresses: ["AMBIGUOUS_MODULE_REF"] },
  DUPLICATE_TYPE: { fixes: [RENAME_DECL, REMOVE_DECL] },
  DUPLICATE_PROCESS: { fixes: [RENAME_DECL, REMOVE_DECL] },
  DUPLICATE_SUBPROCESS: { fixes: [RENAME_DECL, REMOVE_DECL] },
  DUPLICATE_VIEW: { fixes: [RENAME_DECL, REMOVE_DECL] },
  DUPLICATE_INTERFACE: { fixes: [RENAME_DECL, REMOVE_DECL] },
  DUPLICATE_INSTANCE: { fixes: [RENAME_DECL, REMOVE_DECL] },
  DUPLICATE_DECORATION: { fixes: [RENAME_DECL, REMOVE_DECL] },
  DECORATION_REDECLARES_BUILTIN: { fixes: [RENAME_DECL] },
  DEFAULT_INTERFACE_DUPLICATE: { fixes: [REMOVE_DECL] },
  AMBIGUOUS_MODULE_REF: { fixes: [QUALIFY_REF] },
  AMBIGUOUS_INTERFACE_LEAF: { fixes: [QUALIFY_REF] },
  IN_TARGET_NOT_FOUND: { fixes: [DECLARE_TARGET, QUALIFY_REF] },
  UNKNOWN_TYPE: { fixes: [DECLARE_TARGET, QUALIFY_REF] },
  UNKNOWN_PARENT_TYPE: { fixes: [DECLARE_TARGET] },
  WRONG_BASE_TYPE: {},
  TYPE_CHANGE_NEEDS_OVERRIDE: { fixes: [ADD_OVERRIDE] },
  TYPE_PROCESS_VIEW_FORBIDDEN: {},
  CASCADE_MODE_REDEFINED: {},
  DROP_NOT_INHERITED: { fixes: [{ id: "remove-drop", target: "model", input: "the `drop` clause" }] },
  OVERRIDE_NOT_INHERITED: {
    fixes: [{ id: "remove-override", target: "model", input: "the member's `override` marker" }],
  },
  ASPECT_DROP_NOT_INHERITED: { fixes: [{ id: "remove-drop", target: "model", input: "the `drop` clause" }] },
  MULTI_OP_ON_TARGET: { fixes: [{ id: "keep-one-op", target: "model", input: "the member operations" }] },
  SURFACE_CONTAINS_MODULE: {
    fixes: [{ id: "move-out-of-surface", target: "model", input: "the containing declaration" }],
  },
  TYPE_NOT_VISIBLE_IN_FILE: { fixes: [{ id: "add-use", target: "model", input: "the file's `use` clauses" }] },
  CROSS_SPACE_TYPE_NOT_VISIBLE: { fixes: [EXPORT_DECL] },
  CROSS_SPACE_COUPLING: { fixes: [EXPORT_DECL] },
  INSTANCE_NAME_COLLISION: { fixes: [RENAME_DECL] },
  INSTANCE_SHADOWS_MODULE: { fixes: [RENAME_DECL] },
  UNNAMED_BASE_MODULE: { fixes: [RENAME_DECL] },
  UNNAMED_NON_MODULE: { fixes: [RENAME_DECL] },
  UNNAMED_DUPLICATE_TYPE: { fixes: [RENAME_DECL] },
  UNNAMED_BAD_DERIVED_NAME: { fixes: [RENAME_DECL] },
  RESERVED_NAME: { fixes: [RENAME_DECL] },
  SNAKE_NO_TAIL: { fixes: [RENAME_DECL] },

  // --- Fields, aspects, descriptions -----------------------------------------
  REQUIRED_NOT_FILLED: { fixes: [FILL_REQUIRED] },
  REQUIRED_HAS_CONTENT: {},
  WIDGET_TAG_INVALID: {},
  ASPECT_BAD_REF: { fixes: [QUALIFY_REF, DECLARE_TARGET] },
  ASPECT_INSTANCE_ON_STRING: {},
  ASPECT_INSTANCE_NOT_MODULE: {},
  ASPECT_SAME_PLANE: {},
  PLANE_MISMATCH: {},
  PRESENCE_MEMBER_COLLISION: {},
  PRESENCE_REF_UNRESOLVED: { fixes: [DECLARE_TARGET] },
  INCLUSION_UNRESOLVED: { fixes: [DECLARE_TARGET] },
  ROSTER_MEMBER_CONFLICT: {},
  DESC_UNRESOLVED_REF: { fixes: [QUALIFY_REF] },
  DESC_AMBIGUOUS_REF: { fixes: [QUALIFY_REF] },
  DESC_MISSING_FIELD: {},
  DESC_MISSING_ASPECT: {},
  DESC_CALC_ERROR: {},
  DOC_BARE_INTERPOLATION: {},
  FIELD_EXPR_UNSUPPORTED: {},
  FIELD_REF_CYCLE: {},
  FIELD_GETTER_MISSING: {},
  FIELD_CAST_BAD_ARG: {},
  FIELD_CAST_NEEDS_VIEW: {},
  FIELD_COLOR_NEEDS_CAST: {},
  FIELD_UNKNOWN_COLOR: {},

  // --- Decorations & lifecycle -----------------------------------------------
  DECORATION_UNKNOWN: { fixes: [DECLARE_TARGET] },
  DECORATION_BAD_TARGET: {},
  DECORATION_DUPLICATE_ARG: {},
  DECORATION_HOMONYM: { fixes: [QUALIFY_REF] },
  DECORATION_UNKNOWN_ARG: {},
  DECORATION_RESERVED_FIELD: { fixes: [RENAME_DECL] },
  LIFECYCLE_DUPLICATE: { fixes: [REMOVE_DECL] },
  // An unparseable date is why the window looks empty or inverted.
  LIFECYCLE_MALFORMED_DATE: { suppresses: ["LIFECYCLE_WINDOW_EMPTY", "LIFECYCLE_WINDOW_INVERTED"] },
  LIFECYCLE_MIXED_MARKERS: {},
  LIFECYCLE_WINDOW_EMPTY: {},
  LIFECYCLE_WINDOW_INVERTED: {},
  LIFECYCLE_OUTLIVES_CONTAINER: {},
  LIFECYCLE_REF_DEPRECATED: {},
  LIFECYCLE_REF_PLANNED: {},
  LIFECYCLE_REF_RETIRED: {},
  LATENCY_MALFORMED: {},
  LATENCY_INCOMPLETE: {},

  // --- Processes -------------------------------------------------------------
  PROCESS_BAD_CALLER: { fixes: [QUALIFY_REF, DECLARE_TARGET] },
  PROCESS_CALLER_TODO: { fixes: [DECLARE_TARGET] },
  PROCESS_CALLEE_TODO: { fixes: [QUALIFY_REF, DECLARE_TARGET] },
  PROCESS_CALLEE_UNSPECIFIED: { fixes: [DECLARE_TARGET] },
  PROCESS_BAD_DO_TARGET: { fixes: [QUALIFY_REF, DECLARE_TARGET] },
  PROCESS_DO_PROCESS_CALLER: {},
  PROCESS_DO_SHADOWED: { fixes: [QUALIFY_REF] },
  PROCESS_DUPLICATE_AS: { fixes: [RENAME_DECL] },
  PROCESS_FIELD_AFTER_ACTION: {},
  PROCESS_FINISH_ALL_NO_COMPENSATION: {},
  PROCESS_GO_TODO: { fixes: [DECLARE_TARGET] },
  PROCESS_GO_UNREACHABLE: {},
  PROCESS_OWNER_TODO: { fixes: [DECLARE_TARGET] },
  PROCESS_ROLE_CONFLICT: {},
  PROCESS_ANON_HEAD_AT_ROOT: {},
  SURFACE_ON_IGNORED: {},

  // --- Process tests ---------------------------------------------------------
  // An untestable process is why its pins match nothing.
  "test.untestable-process": {
    suppresses: ["test.unmatched-pin", "test.unmatched-trigger", "test.unmatched-fail"],
  },
  "test.no-target": {},
  "test.unmatched-pin": {},
  "test.unrecognized-pin": {},
  "test.conflicting-await-pin": {},
  "test.unmatched-trigger": {},
  "test.ambiguous-trigger": {},
  "test.unmatched-fail": {},
  "test.ambiguous-choice-key": {},
  "test.trace-cap": {},
  "test.capture-untestable": { suppresses: ["test.capture-ambiguous"] },
  "test.capture-ambiguous": {},
  "test.assertion-fail": {},
  "test.assertion-open": {},

  // --- Views -----------------------------------------------------------------
  VIEW_ON_NO_PRESENCE: {},
  VIEW_UNKNOWN_PLANE: {},
  VIEW_UNKNOWN_PARENT: { fixes: [QUALIFY_REF] },
  VIEW_UNKNOWN_IN_TARGET: { fixes: [QUALIFY_REF] },
  VIEW_UNKNOWN_BUNDLE: {},
  VIEW_UNKNOWN_POLICY: { fixes: [QUALIFY_REF] },
  VIEW_UNKNOWN_KNOB: {},
  VIEW_KNOB_TYPE: {},
  VIEW_KNOB_RANGE: {},
  VIEW_KNOB_SET: {},
  VIEW_EMPTY_FOCUS: {},
  VIEW_SELECTOR_ERROR: {},
  VIEW_CSS_UNPARSEABLE: {},
  VIEW_COLOR_NEEDS_CAST: {},
  VIEW_MULTIPLE_REPRESENTATIONS: {},
  VIEW_MULTIPLE_STORIES: {},
  VIEW_EMPTY_STORY_CHAPTER: {},
  /** A view's plan threw — the board falls back; nothing mechanical repairs it. */
  VIEW_PLAN_ERROR: {},
  BPMN_LANE_EMPTY: {},
  BPMN_LANE_BAD_SELECTOR: { fixes: [QUALIFY_REF] },
  BPMN_LANE_MULTI_VALUE: {},
  BPMN_POOLS_ONLY: {},

  // --- Governance ------------------------------------------------------------
  // A violation is repaired either at the subject (make the model satisfy the
  // rule) or at the policy instance (`except … "<reason>"` — a waiver is a
  // recorded decision, never a silent drop).
  POLICY_VIOLATION: {
    fixes: [
      { id: "satisfy-policy", target: "model", input: "the flagged element's body" },
      { id: "waive-finding", target: "model", input: "an `except <selector> \"<reason>\"` clause" },
    ],
  },
  // Pack adoption (`policies { <pkg>: "<version>" }`). A pack that can't resolve
  // is the root cause of both the version check and every dropped-policy report
  // it would otherwise emit.
  POLICY_PACK_NOT_FOUND: {
    fixes: [{ id: "add-dependency", target: "model", input: "package.archspace dependencies" }],
    suppresses: ["POLICY_PACK_VERSION_MISMATCH", "POLICY_PACK_DROPPED"],
  },
  POLICY_PACK_VERSION_MISMATCH: {
    fixes: [{ id: "repin-pack-version", target: "model", input: "package.archspace policies pin" }],
  },
  POLICY_PACK_DROPPED: {
    fixes: [
      { id: "restore-use-policy", target: "model", input: "a `use policy <Name> from <pack>` statement" },
      { id: "drop-pack-adoption", target: "model", input: "package.archspace policies block" },
    ],
  },
  POLICY_REQUIRE_IGNORES_SUBJECT: {},
  POLICY_FORBID_IGNORES_NONCARRIERS: {},
  POLICY_DEAD_WAIVER: { fixes: [{ id: "remove-waiver", target: "model", input: "the waiver clause" }] },
  WAIVER_REQUIRES_REVIEW: {},
  ABSENT_CAPABILITY: {},
  SLO_P95_BREACH: {},

  // --- Live conformance (@archlang/otel-conform) -----------------------------
  // No telemetry at all is the root cause of every "modelled but not observed"
  // finding the same run would otherwise report.
  "otel-conform/no-data-precondition-absent": {
    suppresses: ["otel-conform/digest-no-store-edges", "otel-conform/digest-noise-name-only"],
  },
  "otel-conform/no-data-empty": {
    suppresses: ["otel-conform/digest-no-store-edges", "otel-conform/digest-noise-name-only"],
  },
  "otel-conform/no-data-undefined": {
    suppresses: ["otel-conform/digest-no-store-edges", "otel-conform/digest-noise-name-only"],
  },
  "otel-conform/traces-capped": {},
  "otel-conform/duplicate-service": {},
  "otel-conform/digest-dimension-b-deferred": {},
  "otel-conform/digest-noise-name-only": {},
  "otel-conform/digest-no-store-edges": {},
  "otel-conform/digest-inventory-endpoints-only": {},

  // --- Evidence bindings (§11.1) ---------------------------------------------
  // `sources: "path:12-88"` attests a module against a pinned commit. Every code
  // here is a WARNING: evidence is review input, and an error would suppress the
  // four unrelated diagnostic families the LSP drops on a "broken" model.
  // The two repo-level codes are root causes — if the pin is absent, the repo
  // unreadable or the wrong one, every per-entry verdict below is meaningless,
  // so they suppress the symptoms rather than emitting one per binding.
  EVIDENCE_MALFORMED: {
    fixes: [{ id: "fix-source-ref", target: "model", input: "the `sources` field entry" }],
  },
  EVIDENCE_PIN_MISSING: {
    fixes: [{ id: "pin-evidence-repo", target: "model", input: "package.archspace `repo:`/`commit:`" }],
    suppresses: ["EVIDENCE_PATH_MISSING", "EVIDENCE_RANGE_OUT_OF_FILE"],
  },
  EVIDENCE_PIN_NOT_A_COMMIT: {
    fixes: [{ id: "repin-evidence-commit", target: "model", input: "package.archspace `commit:`" }],
  },
  EVIDENCE_UNVERIFIED: {
    fixes: [{ id: "repin-evidence-commit", target: "model", input: "package.archspace `commit:`" }],
    suppresses: ["EVIDENCE_PATH_MISSING", "EVIDENCE_RANGE_OUT_OF_FILE"],
  },
  EVIDENCE_ORIGIN_MISMATCH: {
    fixes: [{ id: "correct-evidence-repo", target: "model", input: "package.archspace `repo:`" }],
    suppresses: ["EVIDENCE_PATH_MISSING", "EVIDENCE_RANGE_OUT_OF_FILE"],
  },
  EVIDENCE_PATH_MISSING: {
    fixes: [
      { id: "fix-source-ref", target: "model", input: "the `sources` field entry" },
      { id: "repin-evidence-commit", target: "model", input: "package.archspace `commit:`" },
    ],
  },
  EVIDENCE_RANGE_OUT_OF_FILE: {
    fixes: [
      { id: "fix-source-ref", target: "model", input: "the `sources` field entry" },
      { id: "repin-evidence-commit", target: "model", input: "package.archspace `commit:`" },
    ],
  },

  // --- Composed geometry -----------------------------------------------------
  // The scene quality predicates. These are the codes whose repairs are SOLVER
  // inputs: the author placed nothing, so "move it" is not a repair that exists.
  GEOMETRY_LABEL_ROUTE_CLEARANCE: { fixes: [RAISE_SEPARATION, RESERVE_CORRIDOR] },
  GEOMETRY_ROUTE_THROUGH_NODE: { fixes: [RESERVE_CORRIDOR, RAISE_SEPARATION, PIN_NODE] },
  GEOMETRY_PROPER_ROUTE_CROSSINGS: { fixes: [RAISE_SEPARATION] },
  GEOMETRY_AMBIGUOUS_COLLINEAR_CORRIDOR: { fixes: [RAISE_SEPARATION] },
  GEOMETRY_CONTAINER_BORDER_RUN: { fixes: [RAISE_SEPARATION, RESERVE_CORRIDOR] },
  GEOMETRY_ROUTE_RHYTHM: { fixes: [RAISE_SEPARATION] },
  GEOMETRY_ENDPOINT_SIDE_DIRECTION: { fixes: [RESERVE_CORRIDOR] },
  // Never "shrink the type": a label is readable or the node grows (§10.2).
  GEOMETRY_PROJECTED_TEXT_FLOOR: { fixes: [GROW_NODE] },
  // §10.2 step 3. The one geometry code whose repair is a MODEL edit: the card
  // would have grown to fit its name, and only the author's own `widget.width`
  // pin refused it — so "widen or drop the pin" is a real `.arch` change.
  CARD_NAME_PIN_TOO_NARROW: {
    fixes: [{ id: "widen-width-pin", target: "model", input: "the instance's `widget.width` pin" }],
  },

  // --- Share cards -----------------------------------------------------------
  // The card request names a snapshot the current model no longer matches. No
  // mechanical repair: the caller asks for a different anchor, or re-cuts the
  // card. Registered here because `DiagnosticCode` is what a receipt's `code`
  // must be — an unregistered code cannot reach a consumer, and the server's
  // refusal path emits this one (`server/src/render-receipt.ts`).
  CARD_SNAPSHOT_MISMATCH: {},
} as const satisfies Record<string, DiagCodeSpec>;

/** Every code any ArchLang surface may emit. `Diagnostic.code` is this type, so
 *  an unregistered code cannot reach a consumer. */
export type DiagnosticCode = keyof typeof DIAG_CODES;

const CODE_SET: ReadonlySet<string> = new Set(Object.keys(DIAG_CODES));

export function isDiagnosticCode(code: string): code is DiagnosticCode {
  return CODE_SET.has(code);
}

export function diagCodeSpec(code: DiagnosticCode): DiagCodeSpec {
  return DIAG_CODES[code];
}

/** The repairs `code` supports — what an agent or a quick-fix may attempt. */
export function supportedFixes(code: string | undefined): readonly RepairFix[] {
  if (!code || !isDiagnosticCode(code)) return [];
  return (DIAG_CODES[code] as DiagCodeSpec).fixes ?? [];
}

/** The symptom codes `code` is the root cause of. */
export function suppressedCodes(code: string | undefined): readonly string[] {
  if (!code || !isDiagnosticCode(code)) return [];
  return (DIAG_CODES[code] as DiagCodeSpec).suppresses ?? [];
}

/** The scene quality predicate → diagnostic code map, so a geometry finding
 *  reports under a registered code with solver-input repairs attached. Keyed by
 *  `@archlang/scene`'s `PredicateKey` (scene can't depend on the engine). */
export const GEOMETRY_CODE_BY_PREDICATE: Readonly<Record<string, DiagnosticCode>> = {
  labelRouteClearance: "GEOMETRY_LABEL_ROUTE_CLEARANCE",
  routeThroughNode: "GEOMETRY_ROUTE_THROUGH_NODE",
  properRouteCrossings: "GEOMETRY_PROPER_ROUTE_CROSSINGS",
  ambiguousCollinearCorridor: "GEOMETRY_AMBIGUOUS_COLLINEAR_CORRIDOR",
  containerBorderRun: "GEOMETRY_CONTAINER_BORDER_RUN",
  routeRhythm: "GEOMETRY_ROUTE_RHYTHM",
  endpointSideDirection: "GEOMETRY_ENDPOINT_SIDE_DIRECTION",
  projectedTextFloor: "GEOMETRY_PROJECTED_TEXT_FLOOR",
};

// =============================================================================
// Suppression
// =============================================================================

/** The slice of a diagnostic suppression reads. Structural, so the LSP's
 *  `FileDiagnostic` and the engine's `Diagnostic` both pass unchanged. */
interface SuppressibleDiagnostic {
  readonly code?: string;
  readonly source?: { readonly file?: string };
  readonly span?: { readonly file?: string };
}

/**
 * Drop symptom diagnostics whose root cause is present in the SAME file.
 *
 * Scope is per-file deliberately: a root cause explains the wreckage in the
 * file it happened in, and a workspace-wide rule would let one bad manifest
 * silence a diagnostic in a file nobody touched. A diagnostic with no source
 * file is never suppressed and never suppresses.
 */
export function suppressSymptoms<T extends SuppressibleDiagnostic>(diags: readonly T[]): T[] {
  const silenced = new Map<string, Set<string>>();
  let any = false;
  for (const d of diags) {
    const codes = suppressedCodes(d.code);
    if (codes.length === 0) continue;
    const file = fileOf(d);
    if (file === undefined) continue;
    const set = silenced.get(file) ?? new Set<string>();
    for (const c of codes) set.add(c);
    silenced.set(file, set);
    any = true;
  }
  if (!any) return [...diags];
  return diags.filter((d) => {
    if (!d.code) return true;
    const file = fileOf(d);
    if (file === undefined) return true;
    return !silenced.get(file)?.has(d.code);
  });
}

function fileOf(d: SuppressibleDiagnostic): string | undefined {
  return d.source?.file ?? d.span?.file;
}

// =============================================================================
// Repair receipts
// =============================================================================

/**
 * The repair-receipt form of a diagnostic set: symptoms dropped, and each
 * survivor carrying the repairs its code supports. This is what a headless
 * surface puts in its render receipt — an agent looping on a refusal gets the
 * root cause plus the named inputs that fix it, instead of English to re-parse.
 */
export function withRepairs<T extends SuppressibleDiagnostic>(
  diags: readonly T[],
): (T & { supportedFixes?: readonly RepairFix[] })[] {
  return suppressSymptoms(diags).map((d) => {
    const fixes = supportedFixes(d.code);
    return fixes.length > 0 ? { ...d, supportedFixes: fixes } : { ...d };
  });
}
